import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { resolveHeader, resolveSeedVarietyCategories } from '../siteHeader';
import MagneticButton from './MagneticButton';
import HeaderGlassOverlay from './HeaderGlassOverlay';

function SearchIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M20 20L16.5 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** Router `to` for header nav from CMS href */
function navTarget(href) {
  if (!href || href === '#') return '/';
  if (href === '/about' || href === '#about') return '/about';
  if (href === '/history') return '/history';
  if (href.startsWith('/')) return href;
  if (href.startsWith('#')) return { pathname: '/', hash: href.slice(1) };
  return '/';
}

function ChevronDown({ className }) {
  return (
    <svg className={className} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Responsive column count for the Crops (seed categories) mega menu. */
function seedCategoryMegaMenuColClass(count) {
  if (count <= 8) return 'grid-cols-2';
  if (count <= 15) return 'grid-cols-3';
  if (count <= 24) return 'grid-cols-4';
  return 'grid-cols-5';
}

/** In-app routes use `Link`; hash links stay as `<a>`. */
function PrimaryNavLink({ href, className, children, onClick, 'aria-current': ariaCurrent, ...rest }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className} onClick={onClick} aria-current={ariaCurrent} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick} aria-current={ariaCurrent} {...rest}>
      {children}
    </a>
  );
}

/**
 * Fixed glass pill header shared by About, History, News detail, etc.
 * Use `alwaysSolid` when there is no dark hero behind the top of the page (e.g. article on white).
 */
export default function MarketingSiteHeader({ site, isNavLinkCurrent, idPrefix = 'marketing', alwaysSolid = false }) {
  const { pathname } = useLocation();
  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const seedVarietyCategories = useMemo(() => resolveSeedVarietyCategories(site), [site]);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [headerSolid, setHeaderSolid] = useState(false);
  const [openDesktopNavDropdownIndex, setOpenDesktopNavDropdownIndex] = useState(null);
  const desktopPrimaryNavRef = useRef(null);
  const desktopNavHoverCloseTimerRef = useRef(null);

  function clearDesktopNavHoverCloseTimer() {
    if (desktopNavHoverCloseTimerRef.current) {
      clearTimeout(desktopNavHoverCloseTimerRef.current);
      desktopNavHoverCloseTimerRef.current = null;
    }
  }

  function openDesktopNavDropdown(idx) {
    clearDesktopNavHoverCloseTimer();
    setOpenDesktopNavDropdownIndex(idx);
  }

  function scheduleCloseDesktopNavDropdown(idx) {
    if (prefersReducedMotion) return;
    clearDesktopNavHoverCloseTimer();
    desktopNavHoverCloseTimerRef.current = setTimeout(() => {
      desktopNavHoverCloseTimerRef.current = null;
      setOpenDesktopNavDropdownIndex((open) => (open === idx ? null : open));
    }, 180);
  }

  const headerVisualSolid = alwaysSolid || headerSolid;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (alwaysSolid) return undefined;
    const onScroll = () => setHeaderSolid(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [alwaysSolid]);

  useEffect(() => {
    if (openDesktopNavDropdownIndex === null) return undefined;
    const clearDesktopNavDropdown = () => {
      clearDesktopNavHoverCloseTimer();
      setOpenDesktopNavDropdownIndex(null);
    };
    const onDocPointer = (e) => {
      const el = desktopPrimaryNavRef.current;
      if (el && !el.contains(e.target)) clearDesktopNavDropdown();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') clearDesktopNavDropdown();
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDesktopNavDropdownIndex]);

  useEffect(
    () => () => {
      if (desktopNavHoverCloseTimerRef.current) {
        clearTimeout(desktopNavHoverCloseTimerRef.current);
        desktopNavHoverCloseTimerRef.current = null;
      }
    },
    []
  );

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 pt-[max(0.65rem,env(safe-area-inset-top))] duration-300 ${
        headerVisualSolid ? '' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-shell px-4 sm:px-5 md:px-6">
        <div
          className={`relative isolate overflow-visible rounded-full border px-2.5 py-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.14)] backdrop-blur-2xl transition-[background-color,border-color,backdrop-filter] duration-300 sm:px-4 sm:py-2 ${
            headerVisualSolid
              ? 'border-neutral-200/75 bg-white/82 ring-1 ring-black/[0.04]'
              : 'border-white/35 bg-white/12 ring-1 ring-white/25 supports-[backdrop-filter]:bg-white/10'
          }`}
        >
          <HeaderGlassOverlay sticky={headerVisualSolid} />
          <div className="relative z-[1] flex min-h-[3.25rem] items-center justify-between gap-2 sm:min-h-[3.5rem] sm:gap-3">
            <Link
              to="/"
              className="inline-flex min-w-0 max-w-[40%] items-center gap-2 no-underline sm:max-w-none sm:gap-2.5"
            >
              <img
                src={headerSection.logoUrl}
                alt={headerSection.logoAlt}
                className={`h-9 w-9 shrink-0 rounded-full object-cover ring-1 sm:h-10 sm:w-10 ${
                  headerVisualSolid ? 'ring-neutral-200' : 'ring-white/35'
                }`}
                width={120}
                height={120}
                decoding="async"
              />
              <span className="flex min-w-0 flex-col leading-tight min-[400px]:flex-row min-[400px]:items-baseline min-[400px]:gap-1">
                <span
                  className={`truncate text-base font-bold tracking-tight sm:text-lg ${
                    headerVisualSolid ? 'text-brand-forest' : 'text-white'
                  }`}
                >
                  {headerSection.brandPrimary}
                </span>
                <span
                  className={`truncate text-base font-semibold sm:text-lg ${
                    headerVisualSolid ? 'text-brand-forest/85' : 'text-[#eef6b8]'
                  }`}
                >
                  {headerSection.brandSecondary}
                </span>
              </span>
            </Link>

            <nav ref={desktopPrimaryNavRef} className="hidden flex-1 justify-center lg:flex" aria-label="Primary">
              <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-x-0.5 p-0 xl:gap-x-1">
                {headerSection.navLinks.map((link, idx) => {
                  const { label, href, dropdown, dropdownSource, dropdownItems = [] } = link;
                  const isCurrent = isNavLinkCurrent(link, pathname);
                  const isSeedCategoryDropdown =
                    Boolean(dropdown) &&
                    dropdownSource === 'seedCategories' &&
                    seedVarietyCategories.length > 0;
                  const isCustomDropdown =
                    Boolean(dropdown) &&
                    dropdownSource === 'custom' &&
                    Array.isArray(dropdownItems) &&
                    dropdownItems.length > 0;
                  const navBase =
                    'inline-flex items-center gap-0.5 text-sm font-medium no-underline transition-colors xl:text-[0.95rem]';
                  const navHero = isCurrent
                    ? 'rounded-full bg-white/22 px-3 py-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                    : 'rounded-full px-3 py-2 text-white/90 hover:bg-white/15 hover:text-white';
                  const navSolid = isCurrent
                    ? 'rounded-full bg-brand-forest/12 px-3 py-2 font-semibold text-brand-forest'
                    : 'rounded-full px-3 py-2 text-neutral-600 hover:bg-neutral-100/90 hover:text-brand-forest';
                  const navLinkClass = `${navBase} ${headerVisualSolid ? navSolid : navHero}`;
                  const submenuSurface = headerVisualSolid
                    ? 'border border-neutral-200 bg-white shadow-lg ring-1 ring-black/[0.04]'
                    : 'border border-brand-forest bg-brand-forest shadow-xl shadow-black/25';
                  const submenuItem = headerVisualSolid
                    ? 'text-neutral-800 hover:bg-neutral-100'
                    : 'text-white hover:bg-white/15';
                  const cropsMegaSurface = headerVisualSolid
                    ? 'border border-neutral-200/90 bg-white/95 shadow-lg backdrop-blur-xl'
                    : 'border border-white/30 bg-white/75 shadow-xl shadow-black/25 backdrop-blur-2xl';
                  const cropsMegaItem = headerVisualSolid
                    ? 'text-neutral-700 hover:bg-neutral-50 hover:text-brand-forest'
                    : 'text-neutral-700 hover:bg-white/50 hover:text-brand-forest';
                  const ddOpen = openDesktopNavDropdownIndex === idx;
                  const submenuId = `${idPrefix}-header-nav-submenu-${idx}`;

                  return (
                    <li key={`${label}-${href}-${idx}`} className="relative">
                      {isSeedCategoryDropdown ? (
                        <div className="relative">
                          <button
                            type="button"
                            className={`${navLinkClass} cursor-pointer border-0 bg-transparent font-inherit`}
                            aria-current={isCurrent ? 'page' : undefined}
                            aria-haspopup="true"
                            aria-expanded={ddOpen}
                            aria-controls={submenuId}
                            id={`${idPrefix}-header-nav-dd-trigger-${idx}`}
                            onMouseEnter={() => {
                              if (!prefersReducedMotion) openDesktopNavDropdown(idx);
                            }}
                            onMouseLeave={() => scheduleCloseDesktopNavDropdown(idx)}
                            onClick={() =>
                              setOpenDesktopNavDropdownIndex((prev) => (prev === idx ? null : idx))
                            }
                          >
                            {label}
                            <ChevronDown
                              className={`mt-px h-2.5 w-2.5 opacity-75 transition-transform duration-200 ${
                                ddOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          {ddOpen ? (
                            <ul
                              id={submenuId}
                              role="menu"
                              aria-label={`${label} submenu`}
                              onMouseEnter={() => openDesktopNavDropdown(idx)}
                              onMouseLeave={() => scheduleCloseDesktopNavDropdown(idx)}
                              className="absolute left-1/2 top-full z-[10001] m-0 w-max max-w-[min(calc(100vw-2rem),56rem)] -translate-x-1/2 list-none pt-2"
                            >
                              <li
                                role="presentation"
                                className={`m-0 grid list-none gap-x-3 gap-y-0.5 rounded-2xl px-4 py-4 sm:gap-x-4 sm:px-5 sm:py-5 ${cropsMegaSurface} ${seedCategoryMegaMenuColClass(
                                  seedVarietyCategories.length
                                )}`}
                              >
                                {seedVarietyCategories.map((c) => (
                                  <Link
                                    key={c.id}
                                    role="menuitem"
                                    to={`/crops/${encodeURIComponent(c.id)}`}
                                    className={`block min-w-0 truncate rounded-md px-1.5 py-1.5 text-[0.9rem] font-medium leading-snug no-underline transition-colors ${cropsMegaItem}`}
                                    onClick={() => setOpenDesktopNavDropdownIndex(null)}
                                  >
                                    {c.name}
                                  </Link>
                                ))}
                              </li>
                            </ul>
                          ) : null}
                        </div>
                      ) : isCustomDropdown ? (
                        <div
                          className="relative"
                          onMouseEnter={() => {
                            if (prefersReducedMotion) return;
                            if (desktopNavHoverCloseTimerRef.current) {
                              clearTimeout(desktopNavHoverCloseTimerRef.current);
                              desktopNavHoverCloseTimerRef.current = null;
                            }
                            setOpenDesktopNavDropdownIndex(idx);
                          }}
                          onMouseLeave={() => {
                            if (prefersReducedMotion) return;
                            if (desktopNavHoverCloseTimerRef.current) {
                              clearTimeout(desktopNavHoverCloseTimerRef.current);
                            }
                            const leaveIdx = idx;
                            desktopNavHoverCloseTimerRef.current = setTimeout(() => {
                              desktopNavHoverCloseTimerRef.current = null;
                              setOpenDesktopNavDropdownIndex((open) => (open === leaveIdx ? null : open));
                            }, 160);
                          }}
                        >
                          <button
                            type="button"
                            className={`${navLinkClass} cursor-pointer border-0 bg-transparent font-inherit`}
                            aria-current={isCurrent ? 'page' : undefined}
                            aria-haspopup="true"
                            aria-expanded={ddOpen}
                            aria-controls={submenuId}
                            id={`${idPrefix}-header-nav-dd-trigger-${idx}`}
                            onClick={() => setOpenDesktopNavDropdownIndex((prev) => (prev === idx ? null : idx))}
                          >
                            {label}
                            <ChevronDown
                              className={`mt-px h-2.5 w-2.5 opacity-75 transition-transform duration-200 ${
                                ddOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          <ul
                            id={submenuId}
                            role="menu"
                            aria-label={`${label} submenu`}
                            hidden={!ddOpen}
                            className={`absolute left-1/2 top-full z-[10001] mt-1.5 min-w-[13rem] -translate-x-1/2 rounded-xl py-1.5 pt-2.5 ${submenuSurface}`}
                          >
                            {dropdownItems.map((item, j) => (
                              <li key={`${item.label}-${item.href}-${j}`} role="none">
                                <PrimaryNavLink
                                  role="menuitem"
                                  href={item.href}
                                  className={`block px-4 py-2 text-sm font-medium no-underline transition-colors ${submenuItem}`}
                                  onClick={() => setOpenDesktopNavDropdownIndex(null)}
                                >
                                  {item.label}
                                </PrimaryNavLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <Link
                          to={dropdown ? '/' : navTarget(href)}
                          className={navLinkClass}
                          aria-current={isCurrent ? 'page' : undefined}
                        >
                          {label}
                          {dropdown ? <ChevronDown className="mt-px h-2.5 w-2.5 opacity-75" /> : null}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
              <button
                type="button"
                className={`inline-flex h-10 min-h-[44px] min-w-[44px] items-center justify-center rounded-full border-0 p-0 transition-colors sm:h-11 ${
                  headerVisualSolid ? 'text-neutral-700 hover:bg-neutral-100' : 'text-white hover:bg-white/18'
                }`}
                aria-label="Search"
              >
                <SearchIcon className="h-5 w-5" />
              </button>
              <div className="hidden sm:flex sm:shrink-0 sm:items-center">
                <MagneticButton
                  href="/#contact"
                  label="Get In Touch"
                  reducedMotion={prefersReducedMotion}
                  variant={headerVisualSolid ? 'light' : 'hero'}
                  surface={headerVisualSolid ? 'light' : 'dark'}
                  size="sm"
                  className="inline-flex max-w-none scale-[0.94] sm:scale-100"
                />
              </div>
              <Link
                to="/"
                className={`inline-flex h-10 min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-sm font-bold no-underline transition-colors sm:h-11 lg:hidden ${
                  headerVisualSolid ? 'text-neutral-700 hover:bg-neutral-100' : 'text-white hover:bg-white/18'
                }`}
                aria-label="Menu — home"
              >
                ···
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
